import type { ColumnDef } from '@tanstack/react-table';
import { flexRender, getCoreRowModel, useReactTable } from '@tanstack/react-table';
import { ExternalLink } from 'lucide-react';
import { Link } from 'react-router';
import { Button } from '@/components/ui/button';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { DEVICE_MODEL_LABELS } from '@/constants/device';
import { cn } from '@/lib/utils';
import type { IIdentificationLog } from '@/types/production/identification-log';

interface IdentificationLogTableProps {
  data: IIdentificationLog[];
}

const statuses = {
  true: 'text-green-500 bg-green-800/20',
  false: 'text-rose-500 bg-rose-800/20',
};

const textColor = {
  true: 'text-green-800',
  false: 'text-rose-800',
};

const info = {
  true: 'Sucesso',
  false: 'Falha',
};

const columns: ColumnDef<IIdentificationLog>[] = [
  {
    header: 'Status',
    accessorKey: 'status',
    cell: ({ row }) => {
      const s = String(row.original.status) as keyof typeof statuses;
      return (
        <div className="flex items-center gap-1">
          <div className={cn(statuses[s], 'flex-none rounded-full p-1 w-fit')}>
            <div className="h-1.5 w-1.5 rounded-full bg-current" />
          </div>
          <div className={cn('hidden font-semibold sm:block', textColor[s])}>{info[s]}</div>
        </div>
      );
    },
  },
  {
    header: 'Serial',
    accessorKey: 'equipment.serial',
    cell: ({ row }) => {
      const before = row.original.equipment_before.serial ?? '--';
      const after = row.original.equipment_after.serial ?? '--';

      return (
        <div className="flex flex-col">
          <span title={`Antes: ${before}`} className="text-sm text-muted-foreground">
            {before}
          </span>
          <span title={`Depois: ${after}`} className="text-sm font-medium">
            {after}
          </span>
        </div>
      );
    },
  },
  {
    header: 'Modelo',
    accessorKey: 'technology.system_name',
    cell: ({ row }) => (
      <p title={row.original.technology.system_name}>
        {DEVICE_MODEL_LABELS[
          row.original.technology.system_name as keyof typeof DEVICE_MODEL_LABELS
        ] ?? '--'}
      </p>
    ),
  },
  {
    header: 'Data',
    accessorKey: 'created_at',
    cell: ({ row }) => <p>{new Date(row.original.created_at).toLocaleString()}</p>,
  },
  {
    header: 'Ação',
    accessorKey: 'equipment',
    cell: ({ row }) => {
      const { original } = row;
      return (
        <Link to={`/production/logs/identificator/${original.id}`}>
          <Button variant="ghost">
            <ExternalLink aria-hidden="true" className="size-5" />
          </Button>
        </Link>
      );
    },
  },
];

export function IdentificationLogTable({ data }: IdentificationLogTableProps) {
  const table = useReactTable({
    data,
    columns,
    getCoreRowModel: getCoreRowModel(),
  });

  return (
    <div className="rounded-md border w-full">
      <Table>
        <TableHeader>
          {table.getHeaderGroups().map((headerGroup) => (
            <TableRow key={headerGroup.id}>
              {headerGroup.headers.map((header) => (
                <TableHead key={header.id}>
                  {header.isPlaceholder
                    ? null
                    : flexRender(header.column.columnDef.header, header.getContext())}
                </TableHead>
              ))}
            </TableRow>
          ))}
        </TableHeader>

        <TableBody>
          {table.getRowModel().rows.length ? (
            table.getRowModel().rows.map((row) => (
              <TableRow key={row.id}>
                {row.getVisibleCells().map((cell) => (
                  <TableCell key={cell.id}>
                    {flexRender(cell.column.columnDef.cell, cell.getContext())}
                  </TableCell>
                ))}
              </TableRow>
            ))
          ) : (
            <TableRow>
              <TableCell colSpan={columns.length} className="h-24 text-center">
                Nenhum dado encontrado
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>
    </div>
  );
}
